import type { UserInput } from '@skop-app/lib-domain/types'

import { pref, type Prefs } from '@/utils/pref'
import {
  ONBOARDING_STEP,
  type OnboardingDraft,
  type OnboardingSurveyProfile,
} from '@/types/onboarding'

import { readOnboardingPreference } from './onboarding.persistence'
import { getOnboardingStepIndex } from './onboarding.steps'

const SNAPSHOT_STORAGE_KEY = 'skop-onboarding-checkout-v1'
const CHECKOUT_RETURN_STORAGE_KEY = 'skop-onboarding-checkout-return-v1'
const CHECKOUT_RETURN_CHANGED_EVENT = 'skop-onboarding-checkout-return-changed'

const notifyOnboardingCheckoutReturnChanged = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(CHECKOUT_RETURN_CHANGED_EVENT))
}

export const subscribeOnboardingCheckoutReturn = (
  callback: () => void,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined
  }

  const handler = () => callback()
  window.addEventListener(CHECKOUT_RETURN_CHANGED_EVENT, handler)
  return () =>
    window.removeEventListener(CHECKOUT_RETURN_CHANGED_EVENT, handler)
}

export type OnboardingCheckoutSnapshot = {
  draft: OnboardingDraft
  stepIndex: number
}

export type OnboardingCheckoutReturnStatus = 'success' | 'canceled' | 'return'

export type OnboardingCheckoutReturn = {
  status: OnboardingCheckoutReturnStatus
  sessionId: string | null
}

export const saveOnboardingCheckoutSnapshot = (
  snapshot: OnboardingCheckoutSnapshot,
): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      SNAPSHOT_STORAGE_KEY,
      JSON.stringify(snapshot),
    )
  } catch {
    // sessionStorage may be unavailable (private browsing, quota)
  }
}

export const readOnboardingCheckoutSnapshot =
  (): OnboardingCheckoutSnapshot | null => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.sessionStorage.getItem(SNAPSHOT_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as OnboardingCheckoutSnapshot
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        typeof parsed.stepIndex !== 'number' ||
        !parsed.draft ||
        typeof parsed.draft !== 'object'
      ) {
        return null
      }
      return {
        draft: {
          ...parsed.draft,
          createdEntities: parsed.draft.createdEntities ?? {},
        },
        stepIndex: parsed.stepIndex,
      }
    } catch {
      return null
    }
  }

export const clearOnboardingCheckoutSnapshot = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(SNAPSHOT_STORAGE_KEY)
  } catch {
    // sessionStorage may be unavailable
  }
}

export const readAndClearOnboardingCheckoutSnapshot =
  (): OnboardingCheckoutSnapshot | null => {
    const snapshot = readOnboardingCheckoutSnapshot()
    if (snapshot) {
      clearOnboardingCheckoutSnapshot()
    }
    return snapshot
  }

export const persistOnboardingCheckoutReturn = (
  checkoutReturn: OnboardingCheckoutReturn,
): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      CHECKOUT_RETURN_STORAGE_KEY,
      JSON.stringify(checkoutReturn),
    )
  } catch {
    // sessionStorage may be unavailable
  }
}

export const clearOnboardingCheckoutReturn = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(CHECKOUT_RETURN_STORAGE_KEY)
    notifyOnboardingCheckoutReturnChanged()
  } catch {
    // sessionStorage may be unavailable
  }
}

const readPersistedOnboardingCheckoutReturn =
  (): OnboardingCheckoutReturn | null => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.sessionStorage.getItem(CHECKOUT_RETURN_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as OnboardingCheckoutReturn
      if (
        parsed?.status !== 'success' &&
        parsed?.status !== 'canceled' &&
        parsed?.status !== 'return'
      ) {
        return null
      }
      return {
        status: parsed.status,
        sessionId:
          typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
      }
    } catch {
      return null
    }
  }

const readOnboardingCheckoutReturnFromUrl =
  (): OnboardingCheckoutReturn | null => {
    if (typeof window === 'undefined') return null

    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    if (
      checkout !== 'success' &&
      checkout !== 'canceled' &&
      checkout !== 'return'
    ) {
      return null
    }

    return {
      status: checkout,
      sessionId: params.get('session_id'),
    }
  }

/** Pure read for `useSyncExternalStore` — no persistence or notifications. */
export const peekOnboardingCheckoutReturn =
  (): OnboardingCheckoutReturn | null => {
    if (typeof window === 'undefined') return null

    return (
      readOnboardingCheckoutReturnFromUrl() ??
      readPersistedOnboardingCheckoutReturn()
    )
  }

/** Persists URL checkout params to sessionStorage. Call from layout effects only. */
export const captureOnboardingCheckoutReturnFromUrl =
  (): OnboardingCheckoutReturn | null => {
    if (typeof window === 'undefined') return null

    const checkoutReturn = readOnboardingCheckoutReturnFromUrl()
    if (!checkoutReturn) return null

    persistOnboardingCheckoutReturn(checkoutReturn)
    notifyOnboardingCheckoutReturnChanged()
    return checkoutReturn
  }

export const readOnboardingCheckoutReturn =
  (): OnboardingCheckoutReturn | null => {
    captureOnboardingCheckoutReturnFromUrl()
    return peekOnboardingCheckoutReturn()
  }

export const clearOnboardingCheckoutReturnParamsFromUrl = (): void => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const hadReturn =
    url.searchParams.has('checkout') || url.searchParams.has('session_id')
  if (!hadReturn) return

  url.searchParams.delete('checkout')
  url.searchParams.delete('session_id')

  const next = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState(window.history.state ?? null, '', next)
}

export const clearOnboardingCheckoutReturnState = (): void => {
  clearOnboardingCheckoutReturnParamsFromUrl()
  clearOnboardingCheckoutReturn()
  clearOnboardingCheckoutSnapshot()
}

const readSurveyProfileFromPreferences = (
  preferences: Prefs,
): OnboardingSurveyProfile | undefined => {
  const userType = pref(preferences, 'survey-user-type')
  if (!userType) return undefined

  const userUsage = preferences?.find(
    (p) => p.key === 'survey-user-usage',
  )?.values

  return {
    userType,
    userUsage,
    userJob: pref(preferences, 'survey-user-job'),
    userTeamSize: pref(preferences, 'survey-user-team-size'),
  }
}

/**
 * Rebuilds a best-effort onboarding draft from the persisted user
 * preferences. Used as a fallback when the sessionStorage checkout snapshot
 * is unavailable (e.g. lost during the Stripe redirect round-trip).
 */
export const buildOnboardingDraftFromPreferences = (
  preferences: UserInput['preferences'] | undefined,
): OnboardingDraft | null => {
  const payload = readOnboardingPreference(preferences)
  if (!payload?.objective) return null

  return {
    objective: payload.objective,
    startMode: payload.startMode,
    collaborationMode: payload.collaborationMode,
    defaultSaleStatus: payload.defaultSaleStatus,
    surveyProfile: readSurveyProfileFromPreferences(preferences),
    createdEntities: payload.createdEntities ?? {},
  }
}

export type OnboardingCheckoutResumeState = {
  draft: OnboardingDraft
  stepIndex: number
  checkoutStatus: OnboardingCheckoutReturnStatus
}

export const resolveOnboardingStateFromCheckoutReturn = (
  preferences?: UserInput['preferences'],
  options?: { consumeSnapshot?: boolean },
): OnboardingCheckoutResumeState | null => {
  const checkoutReturn = peekOnboardingCheckoutReturn()
  if (!checkoutReturn) return null

  const snapshot = options?.consumeSnapshot
    ? readAndClearOnboardingCheckoutSnapshot()
    : readOnboardingCheckoutSnapshot()
  const draft =
    snapshot?.draft ?? buildOnboardingDraftFromPreferences(preferences)
  if (!draft?.objective) return null

  const saleStatusIndex = getOnboardingStepIndex(
    draft.objective,
    ONBOARDING_STEP.saleStatus,
    draft.surveyProfile,
  )

  if (saleStatusIndex < 0) {
    // An older inventory draft may return from checkout. Inventory no longer
    // has an offer step; resume its first action instead of a stale index.
    return {
      draft,
      stepIndex: getOnboardingStepIndex(
        draft.objective,
        ONBOARDING_STEP.resourceStart,
        draft.surveyProfile,
      ),
      checkoutStatus: checkoutReturn.status,
    }
  }

  return {
    draft,
    stepIndex: saleStatusIndex,
    checkoutStatus: checkoutReturn.status,
  }
}
